<template>
    <div class="p-3">
        <div
            class="d-flex align-items-center justify-content-between gap-2 mb-2"
        >
            <div>
                <h3 class="mb-0">
                    Detail CPPT dan Pengisian ICD Rawat Jalan / IGD
                </h3>
                <small class="text-muted">
                    Riwayat pasien berdasarkan satu nomor registrasi
                </small>
            </div>

            <button
                type="button"
                class="btn btn-sm btn-outline-secondary"
                @click="$router.back()"
            >
                <i class="fa fa-arrow-left me-1"></i>
                Kembali
            </button>
        </div>

        <div v-if="loadingPage" class="text-center py-5">
            <div class="spinner-border text-primary"></div>
            <div class="mt-2 text-muted">Memuat data pasien...</div>
        </div>

        <div v-else-if="errorPage" class="alert alert-danger">
            <div class="fw-semibold">Data gagal dimuat</div>
            <div>{{ errorPage }}</div>

            <button
                type="button"
                class="btn btn-sm btn-outline-danger mt-2"
                @click="loadPage"
            >
                <i class="fa fa-refresh me-1"></i>
                Muat Ulang
            </button>
        </div>

        <template v-else>
            <div class="card border-0 shadow-sm mb-3">
                <div class="card-header bg-primary text-white fw-semibold">
                    <i class="fa fa-user me-1"></i>
                    Data Pasien
                </div>

                <div class="card-body patient-card-body">
                    <div class="patient-grid">
                        <div
                            v-for="item in patientInfo"
                            :key="item.label"
                            class="patient-item"
                            :class="item.className || ''"
                        >
                            <div class="patient-label">
                                {{ item.label }}
                            </div>

                            <div class="patient-value">
                                {{ showValue(item.value) }}
                            </div>
                        </div>
                    </div>

                    <div class="patient-status">
                        <span class="badge bg-info text-dark">
                            Rawat Jalan / IGD
                        </span>
                    </div>
                </div>
            </div>

            <div class="row g-3 align-items-stretch workspace-row">
                <div class="col-12 col-xl-4">
                    <div class="card border-0 shadow-sm icd-card">
                        <div
                            class="card-header bg-primary text-white fw-semibold"
                        >
                            <i class="fa fa-pencil-square-o me-1"></i>
                            Pengisian ICD-10 dan ICD-9
                        </div>

                        <div class="card-body">
                            <div v-if="loading.icd" class="loading-section">
                                <i class="fa fa-spinner fa-spin me-1"></i>
                                Memuat ICD...
                            </div>

                            <template v-else>
                                <div class="alert alert-info py-2 small">
                                    Baris pertama merupakan diagnosis utama.
                                    Baris berikutnya merupakan diagnosis atau
                                    prosedur sekunder.
                                </div>

                                <div
                                    v-for="(row, index) in icdRows"
                                    :key="row.key"
                                    class="icd-row"
                                >
                                    <div
                                        class="d-flex align-items-center justify-content-between gap-2 mb-2"
                                    >
                                        <span
                                            class="badge"
                                            :class="
                                                index === 0
                                                    ? 'bg-primary'
                                                    : 'bg-secondary'
                                            "
                                        >
                                            {{
                                                index === 0
                                                    ? "Diagnosis Utama"
                                                    : `Sekunder ${index}`
                                            }}
                                        </span>

                                        <button
                                            type="button"
                                            class="btn btn-sm btn-outline-danger"
                                            :disabled="savingIcd"
                                            @click="removeIcdRow(index)"
                                        >
                                            <i class="fa fa-trash"></i>
                                        </button>
                                    </div>

                                    <div class="mb-3">
                                        <label
                                            class="form-label small fw-semibold"
                                        >
                                            ICD-10
                                        </label>

                                        <v-select
                                            v-model="row.diagnosa"
                                            :options="icd10Options"
                                            :clearable="true"
                                            :filterable="false"
                                            label="name"
                                            placeholder="Ketik kode atau diagnosis ICD-10..."
                                            @search="searchIcd10"
                                            @input="setIcd10($event, index)"
                                        >
                                            <template #no-options>
                                                Ketik minimal 1 karakter
                                            </template>
                                        </v-select>
                                    </div>

                                    <div>
                                        <label
                                            class="form-label small fw-semibold"
                                        >
                                            ICD-9
                                        </label>

                                        <v-select
                                            v-model="row.procedure"
                                            :options="icd9Options"
                                            :clearable="true"
                                            :filterable="false"
                                            label="name"
                                            placeholder="Ketik kode atau tindakan ICD-9..."
                                            @search="searchIcd9"
                                            @input="setIcd9($event, index)"
                                        >
                                            <template #no-options>
                                                Ketik minimal 1 karakter
                                            </template>
                                        </v-select>
                                    </div>
                                </div>

                                <button
                                    type="button"
                                    class="btn btn-outline-primary btn-sm w-100 mb-2"
                                    :disabled="savingIcd"
                                    @click="addIcdRow"
                                >
                                    <i class="fa fa-plus-circle me-1"></i>
                                    Tambah Diagnosis
                                </button>

                                <button
                                    type="button"
                                    class="btn btn-success btn-sm w-100"
                                    :disabled="savingIcd"
                                    @click="saveIcd"
                                >
                                    <span
                                        v-if="savingIcd"
                                        class="spinner-border spinner-border-sm me-1"
                                    ></span>
                                    <i v-else class="fa fa-save me-1"></i>
                                    {{
                                        savingIcd
                                            ? "Menyimpan..."
                                            : "Simpan ICD"
                                    }}
                                </button>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="col-12 col-xl-8">
                    <div class="card border-0 shadow-sm history-card">
                        <div class="card-header bg-white fw-semibold">
                            <i class="fa fa-folder-open text-success me-1"></i>
                            Riwayat Pelayanan
                        </div>

                        <div class="card-body history-scroll bg-body-tertiary">
                            <RiwayatPelayananIcd
                                v-if="Regno"
                                :key="Regno"
                                :regno="Regno"
                                :medrec="pasien.Medrec"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import RiwayatPelayananIcd from "./RiwayatPelayananIcd.vue";

export default {
    name: "UpdateICDPasien",

    components: {
        "v-select": vSelect,
        RiwayatPelayananIcd,
    },

    data() {
        return {
            Regno: "",
            pasien: {},
            icdRows: [],
            icd10Options: [],
            icd9Options: [],
            loadingPage: true,
            errorPage: "",
            savingIcd: false,
            rowCounter: 0,
            loading: {
                icd: false,
            },
        };
    },

    computed: {
        patientInfo() {
            return [
                { label: "Nama Pasien", value: this.pasien.Firstname },
                { label: "Tanggal Lahir", value: this.pasien.TanggalLahir },
                {
                    label: "Usia",
                    value:
                        this.pasien.Usia !== null &&
                        this.pasien.Usia !== undefined
                            ? `${this.pasien.Usia} tahun`
                            : null,
                },
                {
                    label: "Jenis Kelamin",
                    value: this.gender(this.pasien.Sex),
                },
                { label: "Poli Asal", value: this.pasien.NMPoli },
                { label: "Dokter", value: this.pasien.NmDoc },
                { label: "Cara Bayar", value: this.pasien.NMCbayar },
                { label: "Kategori Pasien", value: this.pasien.NmKategori },
                { label: "No. Registrasi", value: this.pasien.Regno },
                { label: "No. Rekam Medis", value: this.pasien.Medrec },
                { label: "No. SEP", value: this.pasien.NoSep },
                {
                    label: "Waktu Registrasi",
                    value: `${this.pasien.TanggalRegistrasi || ""} ${
                        this.pasien.JamRegistrasi || ""
                    }`.trim(),
                },
                {
                    label: "Diagnosa Awal",
                    value: this.pasien.DiagnosaAwal
                        ? `${this.pasien.KdICD || ""} - ${
                              this.pasien.DiagnosaAwal
                          }`
                        : this.pasien.KdICD,
                    className: "patient-item-wide",
                },
            ];
        },
    },

    created() {
        this.Regno = String(this.$route.params.Regno || "").trim();
    },

    mounted() {
        this.loadPage();
    },

    methods: {
        async loadPage() {
            this.loadingPage = true;
            this.errorPage = "";

            try {
                if (!this.Regno) {
                    throw new Error("Nomor registrasi tidak ditemukan.");
                }

                const response = await this.axios.get(
                    "/api/pasien-icd/get-detail-pasien-icd",
                    {
                        params: {
                            Regno: this.Regno,
                        },
                    }
                );

                if (response.data.status !== "success") {
                    throw new Error(
                        response.data.message || "Data pasien gagal dimuat."
                    );
                }

                this.pasien = response.data.data_pasien || {};
                await this.loadIcd();
            } catch (error) {
                this.errorPage =
                    error.response?.data?.message ||
                    error.message ||
                    "Data pasien gagal dimuat.";
            } finally {
                this.loadingPage = false;
            }
        },

        async loadIcd() {
            this.loading.icd = true;
            this.icdRows = [];

            try {
                const [utamaResponse, sekunderResponse] = await Promise.all([
                    this.axios.get("/api/update-icd/get-icd-utama", {
                        params: {
                            Regno: this.Regno,
                        },
                    }),
                    this.axios.get("/api/update-icd/get-icd-sekunder", {
                        params: {
                            Regno: this.Regno,
                        },
                    }),
                ]);

                const utama = Array.isArray(utamaResponse.data.utama)
                    ? utamaResponse.data.utama
                    : [];

                const sekunder = Array.isArray(sekunderResponse.data.sekunder)
                    ? sekunderResponse.data.sekunder
                    : [];

                [...utama, ...sekunder].forEach((item) => {
                    this.icdRows.push(this.createIcdRow(item));
                });

                if (!this.icdRows.length) {
                    this.addIcdRow();
                }
            } catch (error) {
                this.showError(
                    error.response?.data?.message ||
                        error.message ||
                        "Data ICD gagal dimuat."
                );
            } finally {
                this.loading.icd = false;
            }
        },

        createIcdRow(data = {}) {
            this.rowCounter += 1;

            const kodeIcd10 = String(
                data.KdICD || data.KDICD || data.KDICDX || ""
            ).trim();

            const namaIcd10 = String(
                data.Diagnosa || data.DIAGNOSA || ""
            ).trim();

            const kodeIcd9 = String(
                data.KdTDK || data.KdDTD || data.KDICD9 || ""
            ).trim();

            const namaIcd9 = String(
                data.Tindakan || data.DiagnosaTindakan || ""
            ).trim();

            return {
                key: `${Date.now()}-${this.rowCounter}`,
                Regno: this.Regno,
                Tanggal: data.Tanggal || this.currentDateTime(),
                KDICDX: kodeIcd10,
                KDICD9: kodeIcd9,
                diagnosa: kodeIcd10
                    ? {
                          id: kodeIcd10,
                          KDICD: kodeIcd10,
                          diagnosa: namaIcd10,
                          name: namaIcd10
                              ? `${kodeIcd10} - ${namaIcd10}`
                              : kodeIcd10,
                      }
                    : null,
                procedure: kodeIcd9
                    ? {
                          id: kodeIcd9,
                          KDICD: kodeIcd9,
                          diagnosa: namaIcd9,
                          name: namaIcd9
                              ? `${kodeIcd9} - ${namaIcd9}`
                              : kodeIcd9,
                      }
                    : null,
            };
        },

        addIcdRow() {
            this.icdRows.push(this.createIcdRow());
        },

        removeIcdRow(index) {
            this.icdRows.splice(index, 1);

            if (!this.icdRows.length) {
                this.addIcdRow();
            }
        },

        setIcd10(value, index) {
            if (!this.icdRows[index]) {
                return;
            }

            this.icdRows[index].diagnosa = value;
            this.icdRows[index].KDICDX = value
                ? String(value.id || value.KDICD || "").trim()
                : "";
        },

        setIcd9(value, index) {
            if (!this.icdRows[index]) {
                return;
            }

            this.icdRows[index].procedure = value;
            this.icdRows[index].KDICD9 = value
                ? String(value.id || value.KDICD || "").trim()
                : "";
        },

        async searchIcd10(search, loading) {
            await this.searchMasterIcd(search, loading, "icd10");
        },

        async searchIcd9(search, loading) {
            await this.searchMasterIcd(search, loading, "icd9");
        },

        async searchMasterIcd(search, loading, type) {
            const query = String(search || "").trim();

            if (typeof loading === "function") {
                loading(true);
            }

            if (!query) {
                if (type === "icd10") {
                    this.icd10Options = [];
                } else {
                    this.icd9Options = [];
                }

                if (typeof loading === "function") {
                    loading(false);
                }

                return;
            }

            try {
                const endpoint =
                    type === "icd10"
                        ? "/api/master-icdx/get-icdx"
                        : "/api/master-icd9/get-icd9";

                const response = await this.axios.get(endpoint, {
                    params: {
                        q: query,
                    },
                });

                const source =
                    response.data.data_icdx ||
                    response.data.data_icd9 ||
                    response.data.data ||
                    response.data.result ||
                    [];

                const options = this.normalizeIcdOptions(source);

                if (type === "icd10") {
                    this.icd10Options = options;
                } else {
                    this.icd9Options = options;
                }
            } catch (error) {
                if (type === "icd10") {
                    this.icd10Options = [];
                } else {
                    this.icd9Options = [];
                }
            } finally {
                if (typeof loading === "function") {
                    loading(false);
                }
            }
        },

        normalizeIcdOptions(source) {
            const rows = Array.isArray(source)
                ? source
                : source?.data || source?.items || [];

            return rows
                .map((item) => {
                    const kode = String(
                        item.id ||
                            item.KDICD ||
                            item.KdICD ||
                            item.kode ||
                            item.value ||
                            ""
                    ).trim();

                    const diagnosa = String(
                        item.diagnosa ||
                            item.Diagnosa ||
                            item.DIAGNOSA ||
                            item.description ||
                            item.text ||
                            item.label ||
                            ""
                    ).trim();

                    return {
                        ...item,
                        id: kode,
                        KDICD: kode,
                        diagnosa,
                        name: diagnosa ? `${kode} - ${diagnosa}` : kode,
                    };
                })
                .filter((item) => item.id);
        },

        validateIcd() {
            const rows = this.icdRows.filter((row) => {
                return (
                    String(row.KDICDX || "").trim() !== "" ||
                    String(row.KDICD9 || "").trim() !== ""
                );
            });

            if (!rows.length) {
                return "Pilih minimal satu diagnosis atau tindakan.";
            }

            if (!String(rows[0].KDICDX || "").trim()) {
                return "Diagnosis utama ICD-10 wajib diisi.";
            }

            return null;
        },

        async saveIcd() {
            if (this.savingIcd) {
                return;
            }

            const validation = this.validateIcd();

            if (validation) {
                this.showError(validation);
                return;
            }

            const payload = this.icdRows
                .filter((row) => {
                    return (
                        String(row.KDICDX || "").trim() !== "" ||
                        String(row.KDICD9 || "").trim() !== ""
                    );
                })
                .map((row) => ({
                    Regno: this.Regno,
                    Tanggal: row.Tanggal || this.currentDateTime(),
                    KDICDX: String(row.KDICDX || "").trim(),
                    KDICD9: String(row.KDICD9 || "").trim(),
                    Action: "",
                }));

            this.savingIcd = true;

            try {
                const response = await this.axios.post(
                    "/api/update-icd/save-icd",
                    {
                        data_icd: payload,
                    }
                );

                if (response.data.status !== "success") {
                    throw new Error(
                        response.data.message || "Data ICD gagal disimpan."
                    );
                }

                await this.$swal.fire({
                    title: "Berhasil",
                    text:
                        response.data.message || "Data ICD berhasil disimpan.",
                    icon: "success",
                    confirmButtonText: "OK",
                });

                await this.loadIcd();
            } catch (error) {
                this.showError(
                    error.response?.data?.message ||
                        error.message ||
                        "Data ICD gagal disimpan."
                );
            } finally {
                this.savingIcd = false;
            }
        },

        gender(value) {
            const gender = String(value || "")
                .trim()
                .toUpperCase();

            if (["L", "M"].includes(gender)) {
                return "Laki-laki";
            }

            if (["P", "F"].includes(gender)) {
                return "Perempuan";
            }

            return value;
        },

        showValue(value) {
            return value === null || value === undefined || value === ""
                ? "-"
                : value;
        },

        currentDateTime() {
            const now = new Date();
            const pad = (value) => String(value).padStart(2, "0");

            return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(
                now.getDate()
            )} ${pad(now.getHours())}:${pad(now.getMinutes())}:${pad(
                now.getSeconds()
            )}`;
        },

        showError(message) {
            this.$swal.fire({
                title: "Error",
                text: message,
                icon: "error",
                confirmButtonText: "OK",
            });
        },
    },
};
</script>

<style scoped>
.patient-card-body {
    padding: 18px 22px 16px;
}

.patient-grid {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    grid-auto-rows: minmax(54px, auto);
    column-gap: 38px;
    row-gap: 8px;
    align-items: start;
}

.patient-item {
    display: grid;
    grid-template-rows: 18px minmax(25px, auto);
    min-width: 0;
    align-content: start;
}

.patient-item-wide {
    grid-column: span 3;
}

.patient-label {
    height: 18px;
    color: #6c757d;
    font-size: 0.76rem;
    line-height: 18px;
}

.patient-value {
    min-width: 0;
    min-height: 25px;
    color: #212529;
    font-size: 0.9rem;
    font-weight: 600;
    line-height: 1.35;
    overflow-wrap: anywhere;
}

.patient-status {
    margin-top: 8px;
}

.workspace-row {
    min-height: 560px;
    margin-right: 0;
    margin-left: 0;
}

.icd-card {
    height: calc(100vh - 340px);
    min-height: 560px;
    overflow: visible;
}

.history-card {
    display: flex;
    height: calc(100vh - 340px);
    min-height: 560px;
    flex-direction: column;
    overflow: hidden;
}

.history-scroll {
    min-height: 0;
    flex: 1 1 auto;
    overflow-x: hidden;
    overflow-y: auto;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
}

.icd-row {
    padding: 12px;
    margin-bottom: 12px;
    background: #fff;
    border: 1px solid #dee2e6;
    border-radius: 6px;
}

.loading-section {
    padding: 16px;
    color: #6c757d;
}

:deep(.vs__dropdown-menu) {
    z-index: 9999;
    max-height: 300px;
}

@media (max-width: 1199.98px) {
    .patient-grid {
        grid-template-columns: repeat(2, minmax(0, 1fr));
        column-gap: 26px;
    }

    .patient-item-wide {
        grid-column: span 2;
    }

    .icd-card,
    .history-card {
        height: auto;
        min-height: auto;
    }

    .history-card {
        overflow: visible;
    }

    .history-scroll {
        overflow: visible;
    }
}

@media (max-width: 767.98px) {
    .patient-grid {
        grid-template-columns: 1fr;
        gap: 10px;
    }

    .patient-item-wide {
        grid-column: span 1;
    }
}
</style>
